"use client";
/**
 * SANATAN DECODE — style proof, 20s @ 30fps, 1280x720.
 * Vox-style motion-graphics kit test on the गोत्र script.
 * Modules exercised: KineticLine · LineageTree · ShastraSlab · AlleleDiagram
 * Silent by design — no TTS spend. Timings are the 130wpm script beats.
 */
import React from "react";
import {
  AbsoluteFill,
  Sequence,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
} from "remotion";
import { loadFont as loadDeva } from "@remotion/google-fonts/NotoSansDevanagari";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: deva } = loadDeva("normal", {
  weights: ["400", "600", "700", "800", "900"],
  subsets: ["devanagari", "latin"],
});
const { fontFamily: inter } = loadInter("normal", {
  weights: ["500", "700", "800"],
  subsets: ["latin"],
});

/* ── locked palette ───────────────────────────────────────────── */
const INK = "#0D1230";
const INK2 = "#1A2250";
const SAFFRON = "#F0A44A";
const CREAM = "#F4EBDA";
const RED = "#E0523C";
const MUTED = "#7C86B8";

/* ── paper grain + vignette, static so it costs nothing ───────── */
const Grain: React.FC = () => (
  <AbsoluteFill style={{ pointerEvents: "none" }}>
    <svg width="100%" height="100%" style={{ opacity: 0.07, mixBlendMode: "overlay" }}>
      <filter id="grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="3" />
      </filter>
      <rect width="100%" height="100%" filter="url(#grain)" />
    </svg>
    <AbsoluteFill
      style={{
        background:
          "radial-gradient(ellipse 70% 60% at 50% 45%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)",
      }}
    />
  </AbsoluteFill>
);

const Ground: React.FC = () => (
  <AbsoluteFill
    style={{
      background: `linear-gradient(165deg, ${INK2} 0%, ${INK} 55%, #070A1C 100%)`,
    }}
  />
);

/* Persistent brand furniture: corner wordmark + bottom hairline. */
const Furniture: React.FC = () => {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();
  const p = interpolate(frame, [0, durationInFrames], [0, 1]);
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <div
        style={{
          position: "absolute",
          top: 34,
          left: 46,
          display: "flex",
          alignItems: "center",
          gap: 10,
          opacity: 0.72,
        }}
      >
        <div style={{ width: 5, height: 22, background: SAFFRON, borderRadius: 3 }} />
        <span
          style={{
            fontFamily: deva,
            fontWeight: 700,
            fontSize: 19,
            letterSpacing: 3,
            color: CREAM,
          }}
        >
          सनातन डिकोड
        </span>
      </div>
      <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: "100%", background: "rgba(255,255,255,0.07)" }} />
      <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: `${p * 100}%`, background: SAFFRON, opacity: 0.85 }} />
    </AbsoluteFill>
  );
};

/* ── MODULE: KineticLine — words land one by one on the beat ──── */
const KineticLine: React.FC<{
  words: string[];
  startAt?: number;
  stagger?: number;
  size?: number;
  color?: string;
  weight?: number;
  redIdx?: number[];
  strikeIdx?: number[];
  strikeAt?: number;
}> = ({
  words,
  startAt = 0,
  stagger = 5,
  size = 62,
  color = CREAM,
  weight = 800,
  redIdx = [],
  strikeIdx = [],
  strikeAt = 0,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  return (
    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "0 18px" }}>
      {words.map((w, i) => {
        const t = startAt + i * stagger;
        const s = spring({ frame: frame - t, fps, config: { damping: 18, stiffness: 150, mass: 0.6 } });
        const isRed = redIdx.includes(i);
        const isStruck = strikeIdx.includes(i);
        const strike = interpolate(frame, [strikeAt, strikeAt + 10], [0, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
          easing: Easing.out(Easing.cubic),
        });
        return (
          <span
            key={i}
            style={{
              position: "relative",
              fontFamily: deva,
              fontWeight: weight,
              fontSize: size,
              lineHeight: 1.42,
              color: isRed ? RED : color,
              opacity: s,
              transform: `translateY(${(1 - s) * 26}px)`,
              display: "inline-block",
            }}
          >
            {w}
            {isStruck && (
              <span
                style={{
                  position: "absolute",
                  left: -4,
                  top: "52%",
                  height: Math.max(4, size * 0.075),
                  width: `calc((100% + 8px) * ${strike})`,
                  background: RED,
                  borderRadius: 4,
                }}
              />
            )}
          </span>
        );
      })}
    </div>
  );
};

const Beat1: React.FC = () => (
  <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", padding: 90 }}>
    <div style={{ textAlign: "center" }}>
      <KineticLine words={["आप", "जिस", "गोत्र", "का", "नाम", "लेते", "हैं"]} startAt={4} stagger={5} size={58} color={MUTED} weight={700} />
      <div style={{ height: 26 }} />
      <KineticLine
        words={["वो", "आपका", "सरनेम", "नहीं", "है।"]}
        startAt={52}
        stagger={7}
        size={78}
        weight={900}
        redIdx={[2, 3, 4]}
        strikeIdx={[2]}
        strikeAt={98}
      />
    </div>
  </AbsoluteFill>
);

/* ── MODULE: LineageTree — the blood-line draws downward ──────── */
const Beat2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const TOP = 128;
  const BOT = 560;
  const X = 430;
  const gens = 4;

  const draw = interpolate(frame, [18, 108], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.inOut(Easing.cubic),
  });
  const lineY = TOP + (BOT - TOP) * draw;

  const headS = spring({ frame: frame - 6, fps, config: { damping: 15, stiffness: 140 } });
  const youS = spring({ frame: frame - 104, fps, config: { damping: 14, stiffness: 150 } });
  const labelS = spring({ frame: frame - 116, fps, config: { damping: 18, stiffness: 120 } });

  return (
    <AbsoluteFill>
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <line x1={X} y1={TOP} x2={X} y2={lineY} stroke={SAFFRON} strokeWidth={5} strokeLinecap="round" opacity={0.9} />
        {Array.from({ length: gens }).map((_, i) => {
          const y = TOP + ((BOT - TOP) / (gens + 1)) * (i + 1);
          const on = lineY > y ? 1 : 0;
          const s = spring({ frame: frame - (18 + (i + 1) * 18), fps, config: { damping: 16, stiffness: 170 } });
          return (
            <g key={i} opacity={on * s}>
              <circle cx={X} cy={y} r={11} fill={INK} stroke={SAFFRON} strokeWidth={4} />
              <line x1={X + 26} y1={y} x2={X + 62} y2={y} stroke={MUTED} strokeWidth={2} opacity={0.55} />
              <text x={X + 76} y={y + 7} fill={MUTED} fontFamily={deva} fontSize={20} fontWeight={600}>
                {["पुत्र", "पौत्र", "प्रपौत्र", "…पीढ़ी दर पीढ़ी"][i]}
              </text>
            </g>
          );
        })}
        <circle cx={X} cy={TOP} r={26 * headS} fill={SAFFRON} />
        <circle cx={X} cy={TOP} r={40 * headS} fill="none" stroke={SAFFRON} strokeWidth={2} opacity={0.35} />
        <circle cx={X} cy={BOT} r={22 * youS} fill={CREAM} />
      </svg>

      <div style={{ position: "absolute", left: X + 62, top: TOP - 26, opacity: headS, transform: `translateX(${(1 - headS) * -18}px)` }}>
        <div style={{ fontFamily: deva, fontSize: 40, fontWeight: 900, color: CREAM }}>ऋषि</div>
        <div style={{ fontFamily: deva, fontSize: 20, fontWeight: 600, color: SAFFRON, letterSpacing: 1 }}>गोत्र का मूल</div>
      </div>

      <div style={{ position: "absolute", left: X + 62, top: BOT - 26, opacity: youS, transform: `translateX(${(1 - youS) * -18}px)` }}>
        <div style={{ fontFamily: deva, fontSize: 40, fontWeight: 900, color: CREAM }}>आप</div>
      </div>

      <div
        style={{
          position: "absolute",
          right: 86,
          top: 300,
          textAlign: "right",
          opacity: labelS,
          transform: `translateY(${(1 - labelS) * 22}px)`,
        }}
      >
        <div style={{ fontFamily: deva, fontSize: 46, fontWeight: 900, color: SAFFRON, lineHeight: 1.25 }}>
          एक ही<br />रक्त-रेखा
        </div>
      </div>
    </AbsoluteFill>
  );
};

/* ── MODULE: ShastraSlab — the rule, tagged as shastra ────────── */
const Beat3: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const slab = spring({ frame: frame - 4, fps, config: { damping: 20, stiffness: 110, mass: 0.9 } });
  const tag = spring({ frame: frame - 16, fps, config: { damping: 16, stiffness: 150 } });
  const rule = spring({ frame: frame - 34, fps, config: { damping: 17, stiffness: 130 } });
  const wipe = interpolate(frame, [34, 58], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });

  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <div
        style={{
          width: 940,
          padding: "54px 60px",
          background: "rgba(255,255,255,0.045)",
          borderLeft: `8px solid ${SAFFRON}`,
          borderRadius: 6,
          transform: `scale(${0.94 + slab * 0.06})`,
          opacity: slab,
        }}
      >
        <div
          style={{
            display: "inline-block",
            fontFamily: deva,
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: 4,
            color: INK,
            background: SAFFRON,
            padding: "6px 18px",
            borderRadius: 4,
            opacity: tag,
            transform: `translateY(${(1 - tag) * 12}px)`,
          }}
        >
          शास्त्र
        </div>
        <div style={{ height: 26 }} />
        <div style={{ position: "relative", overflow: "hidden" }}>
          <div
            style={{
              fontFamily: deva,
              fontSize: 84,
              fontWeight: 900,
              color: CREAM,
              opacity: rule,
              clipPath: `inset(0 ${(1 - wipe) * 100}% 0 0)`,
            }}
          >
            सगोत्र विवाह नहीं।
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

/* ── MODULE: AlleleDiagram — the honest mechanism, then the term ─ */
const Beat4: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const pA = spring({ frame: frame - 4, fps, config: { damping: 16, stiffness: 150 } });
  const pB = spring({ frame: frame - 12, fps, config: { damping: 16, stiffness: 150 } });
  const link = interpolate(frame, [26, 44], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  const child = spring({ frame: frame - 44, fps, config: { damping: 14, stiffness: 160 } });
  const term = spring({ frame: frame - 62, fps, config: { damping: 19, stiffness: 110 } });

  const LX = 400;
  const RX = 880;
  const PY = 210;
  const CY = 410;
  const CX = 640;

  const Parent: React.FC<{ x: number; s: number }> = ({ x, s }) => (
    <g opacity={s} transform={`translate(${x},${PY}) scale(${0.85 + s * 0.15})`}>
      <circle r={54} fill="none" stroke={MUTED} strokeWidth={4} />
      <circle cx={-24} cy={0} r={14} fill={MUTED} opacity={0.45} />
      <circle cx={24} cy={0} r={14} fill={RED} />
    </g>
  );

  return (
    <AbsoluteFill>
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Parent x={LX} s={pA} />
        <Parent x={RX} s={pB} />
        <line x1={LX + 30} y1={PY + 46} x2={LX + 30 + (CX - LX - 30) * link} y2={PY + 46 + (CY - PY - 46) * link} stroke={SAFFRON} strokeWidth={4} strokeLinecap="round" />
        <line x1={RX - 30} y1={PY + 46} x2={RX - 30 - (RX - 30 - CX) * link} y2={PY + 46 + (CY - PY - 46) * link} stroke={SAFFRON} strokeWidth={4} strokeLinecap="round" />
        <g opacity={child} transform={`translate(${CX},${CY}) scale(${0.8 + child * 0.2})`}>
          <circle r={54} fill="none" stroke={RED} strokeWidth={5} />
          <circle r={54} fill={RED} opacity={0.1} />
          <circle cx={-24} cy={0} r={14} fill={RED} />
          <circle cx={24} cy={0} r={14} fill={RED} />
        </g>
      </svg>

      <div style={{ position: "absolute", left: 0, right: 0, top: CY + 96, textAlign: "center", opacity: term, transform: `translateY(${(1 - term) * 20}px)` }}>
        <div style={{ fontFamily: inter, fontSize: 46, fontWeight: 800, letterSpacing: 7, color: SAFFRON }}>
          CONSANGUINITY
        </div>
        <div style={{ height: 10 }} />
        <div style={{ fontFamily: deva, fontSize: 30, fontWeight: 700, color: CREAM, opacity: 0.85 }}>
          दो हज़ार साल बाद, विज्ञान ने इसे एक नाम दिया।
        </div>
      </div>
    </AbsoluteFill>
  );
};

/* ── assembly ─────────────────────────────────────────────────── */
export const SanatanDecodeGotraProof: React.FC = () => {
  const frame = useCurrentFrame();
  // one continuous slow push so nothing ever sits perfectly still
  const drift = interpolate(frame, [0, 600], [1, 1.035]);

  return (
    <AbsoluteFill style={{ backgroundColor: INK }}>
      <AbsoluteFill style={{ transform: `scale(${drift})` }}>
        <Ground />
        <Sequence from={0} durationInFrames={150}>
          <Beat1 />
        </Sequence>
        <Sequence from={150} durationInFrames={180}>
          <Beat2 />
        </Sequence>
        <Sequence from={330} durationInFrames={150}>
          <Beat3 />
        </Sequence>
        <Sequence from={480} durationInFrames={120}>
          <Beat4 />
        </Sequence>
      </AbsoluteFill>
      <Grain />
      <Furniture />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
